/**
 * Homepage banners — every image banner on the customer home page, in one list. Structure copied
 * from vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/table.tsx
 * (the list) and vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx
 * (the image + name identity cell, the status cell, the row actions); the placement filter is
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx's `StatusField`,
 * the same way products/filters.tsx uses it.
 *
 * The whole set arrives in one response (a handful of rows), sorted here into home page order — top
 * slot first — and by display order within a slot, which is the order the storefront draws them in.
 * Move up / Move down renumber one placement through `PUT /homepage-banners/order`. Delete is a real
 * delete, so deleting an active banner says what that takes off the page, and offers the switch.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import type { ColumnDef } from "@tanstack/react-table";
import {
  PiArrowDownDuotone as DownIcon,
  PiArrowUpDuotone as UpIcon,
  PiPencilSimpleDuotone as PencilIcon,
  PiPlusBold as Plus,
  PiTrashDuotone as TrashIcon,
} from "react-icons/pi";
import { Badge, Button, Text } from "rizzui";
import { EmptyState, ErrorSurface, StatusField, problemTitle } from "@steelyard/theme";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { RowActions } from "../../../components/table/row-actions";
import { StatusToggle } from "../../../components/table/status-toggle";
import { FILTER_LABEL } from "../../../components/table/toolbar";
import { useConfirm } from "../../../components/confirm-modal";
import { can } from "../../../lib/permissions";
import { formatDate } from "../../../lib/format";
import { hrefWith } from "../../../lib/paging";
import { bannerPath } from "../../../lib/paths";
import { imageUrl } from "@steelyard/common";
import {
  PLACEMENTS,
  deleteBanner,
  inputOf,
  listBanners,
  movedOrder,
  placementLabel,
  reorderBanners,
  scheduleFromApi,
  updateBanner,
  type BannerPlacement,
  type HomepageBanner,
} from "../../../lib/services/banners";
import type { AccountSummary, PermissionState } from "../../../types";

export async function bannersListLoader({ request }: LoaderFunctionArgs) {
  const raw = new URL(request.url).searchParams.get("placement");
  const placement = PLACEMENTS.some((p) => p.value === raw) ? (raw as BannerPlacement) : undefined;
  const result = await listBanners(placement);
  return { banners: homePageOrder(result.items), placement };
}

/** Top slot first, then the storefront's own display order within a slot (the API's). */
export function homePageOrder(rows: readonly HomepageBanner[]): HomepageBanner[] {
  const rank = (row: HomepageBanner) => PLACEMENTS.findIndex((p) => p.value === row.placement);
  return [...rows].sort((a, b) => rank(a) - rank(b));
}

/** Whether a banner is on the page now, waiting for its start day, past its end day, or switched off. */
function shownBadge(row: HomepageBanner) {
  if (!row.isActive) return <Badge variant="flat" color="secondary" rounded="md">Hidden</Badge>;
  if (row.isLive) return <Badge variant="flat" color="success" rounded="md">On the page</Badge>;
  const notYet = row.startsAt !== null && Date.parse(row.startsAt) > Date.now();
  return (
    <Badge variant="flat" color="warning" rounded="md">
      {notYet ? "Scheduled" : "Ended"}
    </Badge>
  );
}

function bannerColumns(
  rows: readonly HomepageBanner[],
  move: (row: HomepageBanner, step: -1 | 1) => void,
  remove: (row: HomepageBanner) => void,
): ColumnDef<HomepageBanner>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: "Banner",
      size: 320,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <img src={imageUrl(row.original.desktopImageHash)} alt="" className="h-12 w-20 shrink-0 rounded border border-muted object-cover" />
          <div className="min-w-0">
            <Text className="truncate font-medium text-gray-900">{row.original.name}</Text>
            {row.original.title ? <Text className="truncate text-xs text-gray-500">{row.original.title}</Text> : null}
          </div>
        </div>
      ),
    },
    { id: "placement", accessorFn: (row) => placementLabel(row.placement), header: "Placement", size: 220 },
    { id: "displayOrder", accessorKey: "displayOrder", header: "Order", size: 90, meta: { align: "right" } },
    {
      id: "schedule",
      accessorFn: (row) => row.startsAt ?? "",
      header: "Shown",
      size: 230,
      cell: ({ row }) => {
        const { start, end } = scheduleFromApi(row.original);
        return (
          <div className="flex flex-col items-start gap-1">
            {shownBadge(row.original)}
            <Text className="text-xs text-gray-500">
              {start || end ? `${start ? formatDate(start) : "Now"} – ${end ? formatDate(end) : "no end date"}` : "No dates set"}
            </Text>
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "isActive",
      header: "Status",
      size: 140,
      cell: ({ row }) => (
        <StatusToggle
          name={row.original.name}
          active={row.original.isActive}
          // No separate route: the status rides PUT with the row's own other fields.
          onChange={(next) => updateBanner(row.original.id, { ...inputOf(row.original), isActive: next })}
        />
      ),
    },
    {
      id: "actions",
      header: "",
      size: 110,
      enableSorting: false,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={{ label: "Edit banner", icon: PencilIcon, to: bannerPath(row.original.slug || row.original.id) }}
          more={[
            movedOrder(rows, row.original.id, -1) && { label: "Move up", icon: UpIcon, onClick: () => move(row.original, -1) },
            movedOrder(rows, row.original.id, 1) && { label: "Move down", icon: DownIcon, onClick: () => move(row.original, 1) },
            { label: "Delete…", icon: TrashIcon, danger: true, onClick: () => remove(row.original) },
          ]}
        />
      ),
    },
  ];
}

export default function BannersList() {
  const { banners, placement } = useLoaderData() as Awaited<ReturnType<typeof bannersListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const confirm = useConfirm();

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="manage the home page banners" />;
  }

  async function move(row: HomepageBanner, step: -1 | 1) {
    const order = movedOrder(banners, row.id, step);
    if (!order) return;
    try {
      await reorderBanners(order);
      toast.success(`${row.name} moved ${step < 0 ? "up" : "down"}`);
      revalidator.revalidate();
    } catch (err) {
      toast.error(problemTitle(err, "Could not change the order"));
    }
  }

  function remove(row: HomepageBanner) {
    confirm({
      title: `Delete ${row.name}`,
      consequence: row.isActive
        ? `This banner is active${row.isLive ? " and on the home page right now" : ""}. Deleting it takes it off the page for good — if it is the only live banner in “${placementLabel(row.placement)}”, that slot shows the template's placeholder. To take it down for now, switch it off instead.`
        : "The banner is deleted. Its images stay in the image store. This cannot be undone.",
      confirmLabel: "Delete banner",
      onConfirm: () =>
        void deleteBanner(row.id).then(
          () => {
            toast.success(`${row.name} deleted`);
            revalidator.revalidate();
          },
          (err: unknown) => toast.error(problemTitle(err, "Could not delete this banner")),
        ),
    });
  }

  const newBanner = (
    <Link to="/website/banners/new">
      <Button as="span">
        <Plus aria-hidden className="me-1.5 size-4" />
        New banner
      </Button>
    </Link>
  );

  const placementOptions = PLACEMENTS.map((p) => ({ value: p.value, label: p.label }));

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Homepage Banners"
        count={banners.length}
        description="Every image banner on the customer home page. A banner shows while it is active and inside its dates; a slot with none shows the template's placeholder."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Website" }, { name: "Homepage Banners" }]}
      />

      <PagedTable<HomepageBanner>
        actions={newBanner}
        // The whole set arrives in one response, paged in memory.
        result={{ items: banners, page: 1, pageSize: banners.length || 1, totalCount: banners.length }}
        caption="Home page banners, with placement, order, dates and status"
        columns={bannerColumns(banners, (row, step) => void move(row, step), remove)}
        getRowId={(row) => row.id}
        filters={
          <StatusField
            label="Placement"
            labelClassName={FILTER_LABEL}
            placeholder="Every placement"
            options={placementOptions}
            value={placement ?? null}
            onChange={(next: string) => navigate(hrefWith("/website/banners", searchParams, { placement: next || undefined, page: undefined }))}
            clearable
            searchable
            onClear={() => navigate(hrefWith("/website/banners", searchParams, { placement: undefined, page: undefined }))}
            getOptionValue={(option) => option.value}
            displayValue={(selected: string) => placementOptions.find((o) => o.value === selected)?.label ?? ""}
            dropdownClassName="!z-10 h-auto"
            className="w-full @5xl:w-72"
          />
        }
        clear={{ active: Boolean(placement), href: "/website/banners" }}
        empty={
          <EmptyState title={placement ? `No banners in ${placementLabel(placement)}` : "No banners yet"} action={newBanner}>
            {placement ? "That slot shows the template's placeholder until one is added." : "Every slot on the home page shows the template's placeholder until a banner is added."}
          </EmptyState>
        }
      />
    </div>
  );
}
